/**
 * Password validation utility
 * Consolidates password strength evaluation logic used across signup forms
 */

/**
 * Password strength criteria
 */
const CRITERIA = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: true,
};

/**
 * Evaluates password strength and returns detailed results
 * @param {string} password - The password to evaluate
 * @returns {Object} - Evaluation results including score, strength, and suggestions
 */
export function evaluatePassword(password) {
  const suggestions = [];
  let score = 0;

  if (!password) {
    return {
      score: 0,
      strength: 'Weak',
      suggestions: [
        'validation.password_min_length',
        'validation.password_uppercase',
        'validation.password_lowercase',
        'validation.password_number',
        'validation.password_special',
      ],
      isStrong: false,
    };
  }

  // Check minimum length
  if (password.length >= CRITERIA.minLength) {
    score++;
  } else {
    suggestions.push('validation.password_min_length');
  }

  // Check for uppercase letter
  if (/[A-Z]/.test(password)) {
    score++;
  } else {
    suggestions.push('validation.password_uppercase');
  }

  // Check for lowercase letter
  if (/[a-z]/.test(password)) {
    score++;
  } else {
    suggestions.push('validation.password_lowercase');
  }

  // Check for number
  if (/\d/.test(password)) {
    score++;
  } else {
    suggestions.push('validation.password_number');
  }

  // Check for special character
  if (/[\W_]/.test(password)) {
    score++;
  } else {
    suggestions.push('validation.password_special');
  }

  // Determine strength level
  let strength;
  if (score >= 5) {
    strength = 'Strong';
  } else if (score >= 3) {
    strength = 'Medium';
  } else {
    strength = 'Weak';
  }

  return {
    score,
    maxScore: 5,
    strength,
    suggestions,
    isStrong: score >= 5,
    percentage: (score / 5) * 100,
  };
}

/**
 * Checks if password is strong enough
 * @param {string} password - The password to check
 * @returns {boolean} - True if password is strong
 */
export function isStrongPassword(password) {
  return evaluatePassword(password).isStrong;
}

/**
 * Gets the color for password strength indicator
 * @param {string} strength - The strength level ('Weak', 'Medium', 'Strong')
 * @returns {string} - MUI color key
 */
export function getStrengthColor(strength) {
  switch (strength) {
    case 'Strong':
      return 'success';
    case 'Medium':
      return 'warning';
    case 'Weak':
      return 'error';
    default:
      return 'inherit';
  }
}

/**
 * Gets translation key for suggestion
 * Maps internal suggestion keys to translation keys
 * @param {string} suggestion - Internal suggestion key
 * @returns {string} - Translation key
 */
export function getSuggestionText(suggestion, t) {
  const SUGGESTION_MAP = {
    'validation.password_min_length': 'Use at least 8 characters',
    'validation.password_uppercase': 'Add at least one uppercase letter',
    'validation.password_lowercase': 'Add at least one lowercase letter',
    'validation.password_number': 'Add at least one number',
    'validation.password_special': 'Add at least one special character',
  };

  if (t && typeof t === 'function') {
    return t(suggestion);
  }

  return SUGGESTION_MAP[suggestion] || suggestion;
}

const passwordValidator = {
  evaluatePassword,
  isStrongPassword,
  getStrengthColor,
  getSuggestionText,
  CRITERIA,
};

export default passwordValidator;
